/**
 * Sales Orders Redux Selectors
 * Memoized selectors for sales-orders data
 */

import { createSelector } from '@reduxjs/toolkit'
import type { SalesOrdersState } from './sales-orders.slice'
import { calculateUtilization } from '../presentation/components/sales-orders-section/utils'

interface RootState {
  salesOrders: SalesOrdersState
}

// Base selectors
export const selectSalesOrdersState = (state: RootState) => state.salesOrders
export const selectSalesOrders = (state: RootState) => state.salesOrders.items
export const selectSelectedSalesOrder = (state: RootState) => state.salesOrders.selectedSalesOrder
export const selectSalesOrderStats = (state: RootState) => state.salesOrders.stats
export const selectSalesOrdersFilter = (state: RootState) => state.salesOrders.filter
export const selectSalesOrdersLoading = (state: RootState) => state.salesOrders.loading
export const selectSalesOrdersError = (state: RootState) => state.salesOrders.error
export const selectSalesOrdersPaginationRaw = (state: RootState) => state.salesOrders.pagination
export const selectSalesOrdersSkuOptions = (state: RootState) => state.salesOrders.skuOptions
export const selectSalesOrdersSkuOptionsLoading = (state: RootState) => state.salesOrders.skuOptionsLoading
export const selectSalesOrdersSkuOptionsError = (state: RootState) => state.salesOrders.skuOptionsError
export const selectSalesOrdersSkuOptionsLoaded = (state: RootState) => state.salesOrders.skuOptionsLoaded

// Pagination selector that uses filter.page (URL-synced) instead of pagination.page (API response)
// This ensures the UI always shows the correct page from URL state
export const selectSalesOrdersPagination = createSelector(
  [selectSalesOrdersPaginationRaw, selectSalesOrdersFilter],
  (pagination, filter) => ({
    ...pagination,
    // Keep UI pagination stable even if API-reported pageCount changes (e.g. after filters refresh).
    // PaginationControls is a controlled component; if filter.page drifts beyond pageCount,
    // it can cause confusing jumps.
    page: Math.min(Math.max(1, filter.page ?? 1), pagination.pageCount ?? 1),
    pageSize: filter.pageSize ?? pagination.pageSize,
  })
)

// Derived selectors
// Note: Filtering is now done server-side via pagination API
// This selector returns the items as-is (already filtered by the API)
export const selectFilteredSalesOrders = createSelector(
  [selectSalesOrders],
  (salesOrders) => salesOrders
)

export const selectActiveSalesOrders = createSelector(
  [selectSalesOrders],
  salesOrders => salesOrders.filter(c => c.status === 'active')
)

export const selectSalesOrderById = createSelector(
  [selectSalesOrders, (_state: RootState, salesOrderId: string) => salesOrderId],
  (salesOrders, salesOrderId) => salesOrders.find(c => c.id === salesOrderId)
)

export const selectSalesOrderUtilization = createSelector(
  [selectSalesOrders],
  salesOrders => salesOrders.map(c => ({
    id: c.id,
    name: c.name,
    utilization: calculateUtilization(c),
  }))
)

// Legacy aliases for backwards compatibility
export const selectContractsState = selectSalesOrdersState
export const selectContracts = selectSalesOrders
export const selectSelectedContract = selectSelectedSalesOrder
export const selectContractStats = selectSalesOrderStats
export const selectContractsFilter = selectSalesOrdersFilter
export const selectContractsLoading = selectSalesOrdersLoading
export const selectContractsError = selectSalesOrdersError
export const selectFilteredContracts = selectFilteredSalesOrders
export const selectActiveContracts = selectActiveSalesOrders
export const selectContractById = selectSalesOrderById
export const selectContractUtilization = selectSalesOrderUtilization
